/**
 * Regras puras do calendário (testadas em calendarUtils.test.ts). Datas sempre como string
 * "AAAA-MM-DD" — nunca `new Date(iso)` (regra do repo: sem conversão de fuso).
 */
import type { Activity } from '@/types/activity';
import type { Board } from '@/types/board';
import { PRIORITY_CHIP, PRIORITY_RANK } from '@/utils/priority';
import { diffDaysISO } from '@/utils/formatters';

/** Agrupa as atividades pelo prazo (chave "AAAA-MM-DD"), abertas antes, por prioridade. */
export const groupActivitiesByDay = (activities: Activity[]): Record<string, Activity[]> => {
  const byDay: Record<string, Activity[]> = {};
  for (const a of activities) {
    if (!a.due_date) continue;
    const key = a.due_date.slice(0, 10);
    (byDay[key] ??= []).push(a);
  }
  for (const list of Object.values(byDay)) {
    list.sort(
      (x, y) =>
        Number(x.is_done) - Number(y.is_done) ||
        PRIORITY_RANK[y.priority] - PRIORITY_RANK[x.priority] ||
        x.title.localeCompare(y.title, 'pt-BR'),
    );
  }
  return byDay;
};

/** Soma `delta` meses a (year, month 0-based), devolvendo o par normalizado. */
export const shiftMonth = (year: number, month: number, delta: number): { year: number; month: number } => {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
};

/** Classes do chip: atrasada em vermelho, concluída riscada, senão pela prioridade. */
export const activityChipClasses = (a: Activity): string => {
  if (a.is_done) return 'bg-slate-50 text-slate-400 border border-slate-200 line-through';
  if (a.is_overdue) return 'bg-red-100 text-red-700 border border-red-300';
  return PRIORITY_CHIP[a.priority];
};

export interface BoardSpan {
  board: Board;
  /** Índice (0-based) do primeiro dia do intervalo coberto pelo quadro. */
  offset: number;
  /** Quantidade de dias cobertos dentro do intervalo. */
  length: number;
  /** O período do quadro começa antes / termina depois do intervalo exibido. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * Posição de um evento/projeto no cronograma do intervalo [rangeStart, rangeEnd]. Quadro sem
 * início usa o fim (e vice-versa); `null` se não tiver período ou não cruzar o intervalo.
 */
export const boardSpanInRange = (board: Board, rangeStart: string, rangeEnd: string): BoardSpan | null => {
  const start = board.start_date?.slice(0, 10) || board.end_date?.slice(0, 10);
  const end = board.end_date?.slice(0, 10) || board.start_date?.slice(0, 10);
  if (!start || !end) return null;
  if (end < rangeStart || start > rangeEnd) return null;
  const from = start < rangeStart ? rangeStart : start;
  const to = end > rangeEnd ? rangeEnd : end;
  return {
    board,
    offset: diffDaysISO(rangeStart, from),
    length: diffDaysISO(from, to) + 1,
    clippedStart: start < rangeStart,
    clippedEnd: end > rangeEnd,
  };
};

/** Spans de todos os quadros, ordenados por início e duração. */
export const boardSpans = (boards: Board[], rangeStart: string, rangeEnd: string): BoardSpan[] =>
  boards
    .map((b) => boardSpanInRange(b, rangeStart, rangeEnd))
    .filter((s): s is BoardSpan => s !== null)
    .sort((a, b) => a.offset - b.offset || b.length - a.length);
